import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import {
  useInvoices,
  useDeleteInvoice,
  useUpdateInvoiceStatus,
  useRestoreInvoice,
} from "../hooks/use-invoices";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowUpDown,
  MoreHorizontal,
  Trash2,
  Send,
  Eye,
  CheckCircle,
  RotateCcw,
} from "lucide-react";
import { useDateFormat } from "@/hooks/use-date-format";
import { useCurrencyFormat } from "@/hooks/use-currency-format";
import type {
  Invoice,
  InvoiceStatus,
  InvoiceType,
} from "../types/invoice.types";

export function InvoiceList() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormat();
  const { formatCurrency } = useCurrencyFormat();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<
    InvoiceType | undefined
  >(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [invoiceToRestore, setInvoiceToRestore] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data: invoiceData, isLoading } = useInvoices({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search:
      (columnFilters.find((f) => f.id === "invoiceNumber")?.value as string) ||
      undefined,
    includeDeleted: showDeleted,
    invoiceType: invoiceTypeFilter,
  });

  const navigate = useNavigate();
  const deleteInvoice = useDeleteInvoice();
  const updateStatus = useUpdateInvoiceStatus();
  const restoreInvoice = useRestoreInvoice();

  const handleDelete = (id: string) => {
    setInvoiceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!invoiceToDelete) return;

    try {
      await deleteInvoice.mutateAsync(invoiceToDelete);
      console.log("✅ Invoice deleted successfully:", invoiceToDelete);
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    } catch (error) {
      console.error("❌ Delete failed:", error);
    }
  };

  const handleRestore = (id: string) => {
    setInvoiceToRestore(id);
    setRestoreDialogOpen(true);
  };

  const confirmRestore = async () => {
    if (!invoiceToRestore) return;

    try {
      await restoreInvoice.mutateAsync(invoiceToRestore);
      console.log("✅ Invoice restored successfully:", invoiceToRestore);
      setRestoreDialogOpen(false);
      setInvoiceToRestore(null);
    } catch (error) {
      console.error("❌ Restore failed:", error);
    }
  };

  const handleView = (id: string) => {
    navigate({ to: `/dashboard/invoices/edit/${id}` });
  };

  const handleStatusChange = async (id: string, newStatus: InvoiceStatus) => {
    await updateStatus.mutateAsync({ id, status: newStatus });
  };

  const getStatusVariant = (
    status: InvoiceStatus,
  ): "default" | "secondary" | "destructive" => {
    switch (status) {
      case "draft":
        return "secondary"; // Grau
      case "sent":
        return "destructive"; // Rot (noch nicht bezahlt)
      case "paid":
        return "default"; // Standard (wird grün über className)
      case "cancelled":
        return "secondary"; // Grau
      case "deleted":
        return "destructive"; // Rot
      default:
        return "default";
    }
  };

  const getStatusClassName = (status: InvoiceStatus): string => {
    if (status === "paid") {
      return "bg-green-100 text-green-800 hover:bg-green-100 border-green-200";
    }
    if (status === "cancelled") {
      return "bg-orange-100 text-orange-800 hover:bg-orange-100 border-orange-200";
    }
    if (status === "deleted") {
      return "bg-red-100 text-red-800 hover:bg-red-100 border-red-200";
    }
    return "";
  };

  const columns: ColumnDef<Invoice>[] = [
    {
      accessorKey: "invoiceNumber",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-1 text-xs sm:px-2 sm:text-sm"
          >
            <span className="hidden sm:inline">
              {t("invoices.invoiceNumber")}
            </span>
            <span className="sm:hidden">Nr.</span>
            <ArrowUpDown className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const status = row.getValue("status") as InvoiceStatus;
        const invoice = row.original;
        return (
          <div className="space-y-0.5">
            {/* Status Badge on mobile - show first */}
            <div className="sm:hidden">
              <Badge
                variant={getStatusVariant(status)}
                className={`text-[10px] ${getStatusClassName(status)}`}
              >
                {t(`invoices.statusLabels.${status}`)}
              </Badge>
            </div>
            <div className="text-xs font-medium sm:text-sm">
              {row.getValue("invoiceNumber")}
            </div>
            {invoice.invoiceType === "free" && (
              <Badge className="border-amber-200 bg-amber-100 text-[10px] text-amber-800 hover:bg-amber-100">
                {t("invoices.typeFree")}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "invoiceDate",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-1 text-xs sm:px-2 sm:text-sm"
          >
            <span className="sm:hidden">Datum</span>
            <span className="hidden sm:inline">{t("invoices.date")}</span>
            <ArrowUpDown className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="text-xs whitespace-nowrap sm:text-sm">
          {formatDate(new Date(row.getValue("invoiceDate")))}
        </div>
      ),
    },
    {
      accessorKey: "dueDate",
      header: t("invoices.dueDate"),
      cell: ({ row }) => (
        <div className="hidden text-xs sm:text-sm md:table-cell">
          {formatDate(new Date(row.getValue("dueDate")))}
        </div>
      ),
      enableHiding: true,
    },
    {
      accessorKey: "total",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-1 text-xs sm:px-2 sm:text-sm"
          >
            <span className="sm:hidden">€</span>
            <span className="hidden sm:inline">{t("invoices.amount")}</span>
            <ArrowUpDown className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const amount = parseFloat(row.getValue("total"));
        return (
          <div className="text-right text-xs font-medium whitespace-nowrap sm:text-sm">
            {formatCurrency(amount)}
          </div>
        );
      },
    },
    {
      accessorKey: "purchaseOrderNumber",
      header: () => (
        <div className="hidden text-xs sm:text-sm lg:table-cell">
          {t("invoices.purchaseOrderNumber")}
        </div>
      ),
      cell: ({ row }) => (
        <div className="hidden text-xs sm:text-sm lg:table-cell">
          {row.getValue("purchaseOrderNumber") || "-"}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: () => (
        <div className="hidden text-xs sm:table-cell sm:text-sm">
          {t("invoices.status")}
        </div>
      ),
      cell: ({ row }) => {
        const status = row.getValue("status") as InvoiceStatus;
        return (
          <div className="hidden sm:table-cell">
            <Badge
              variant={getStatusVariant(status)}
              className={`text-[10px] sm:text-xs ${getStatusClassName(status)}`}
            >
              {t(`invoices.statusLabels.${status}`)}
            </Badge>
          </div>
        );
      },
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const invoice = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-6 w-6 p-0 sm:h-8 sm:w-8"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Menü öffnen</span>
                <MoreHorizontal className="h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t("invoices.actions")}</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(invoice.id);
                }}
              >
                {t("common.copy")} ID
              </DropdownMenuItem>
              <DropdownMenuSeparator />

              {/* Restore option for deleted invoices */}
              {invoice.status === "deleted" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestore(invoice.id);
                  }}
                  className="text-green-600"
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  {t("invoices.restore")}
                </DropdownMenuItem>
              )}

              {/* View option for non-deleted invoices */}
              {invoice.status !== "deleted" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleView(invoice.id);
                  }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  {invoice.status === "draft"
                    ? t("invoices.edit")
                    : t("invoices.view")}
                </DropdownMenuItem>
              )}

              {/* Status change options — not available for free invoices */}
              {invoice.status === "draft" && invoice.invoiceType !== "free" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(invoice.id, "sent");
                  }}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {t("invoices.markAsSent")}
                </DropdownMenuItem>
              )}
              {invoice.status === "sent" && invoice.invoiceType !== "free" && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(invoice.id, "paid");
                  }}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t("invoices.markAsPaid")}
                </DropdownMenuItem>
              )}

              {/* Delete option only for drafts */}
              {invoice.status === "draft" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(invoice.id);
                    }}
                    className="text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("invoices.delete")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: invoiceData?.data || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    pageCount: invoiceData?.pagination
      ? Math.ceil(invoiceData.pagination.total / invoiceData.pagination.limit)
      : 0,
    state: {
      sorting,
      columnFilters,
      pagination,
    },
  });

  if (isLoading) {
    return <div className="p-8 text-center">{t("common.loading")}...</div>;
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input
          placeholder={t("invoices.search")}
          value={
            (table.getColumn("invoiceNumber")?.getFilterValue() as string) ?? ""
          }
          onChange={(event) =>
            table.getColumn("invoiceNumber")?.setFilterValue(event.target.value)
          }
          className="w-full sm:max-w-sm"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            variant={invoiceTypeFilter === "free" ? "default" : "outline"}
            onClick={() =>
              setInvoiceTypeFilter(
                invoiceTypeFilter === "free" ? undefined : "free",
              )
            }
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            {invoiceTypeFilter === "free"
              ? t("invoices.showAll")
              : t("invoices.showFreeOnly")}
          </Button>
          <Button
            variant={showDeleted ? "default" : "outline"}
            onClick={() => setShowDeleted(!showDeleted)}
            size="sm"
            className="flex-1 sm:flex-initial"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {showDeleted
              ? t("invoices.hideDeleted")
              : t("invoices.showDeleted")}
          </Button>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="space-y-3 sm:hidden">
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            const invoice = row.original;
            return (
              <div
                key={row.id}
                onClick={() => handleView(invoice.id)}
                className="bg-card hover:bg-muted/50 cursor-pointer space-y-2 rounded-lg border p-3"
              >
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <Badge
                      variant={getStatusVariant(invoice.status)}
                      className={`text-[10px] ${getStatusClassName(invoice.status)}`}
                    >
                      {t(`invoices.statusLabels.${invoice.status}`)}
                    </Badge>
                    {invoice.invoiceType === "free" && (
                      <Badge className="ml-1 border-amber-200 bg-amber-100 text-[10px] text-amber-800 hover:bg-amber-100">
                        {t("invoices.typeFree")}
                      </Badge>
                    )}
                    <div className="text-sm font-medium">
                      {invoice.invoiceNumber}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>
                        {t("invoices.actions")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(invoice.id);
                        }}
                      >
                        {t("common.copy")} ID
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />

                      {invoice.status === "deleted" && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(invoice.id);
                          }}
                          className="text-green-600"
                        >
                          <RotateCcw className="mr-2 h-4 w-4" />
                          {t("invoices.restore")}
                        </DropdownMenuItem>
                      )}

                      {invoice.status !== "deleted" && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleView(invoice.id);
                          }}
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          {invoice.status === "draft"
                            ? t("invoices.edit")
                            : t("invoices.view")}
                        </DropdownMenuItem>
                      )}

                      {invoice.status === "draft" &&
                        invoice.invoiceType !== "free" && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(invoice.id, "sent");
                            }}
                          >
                            <Send className="mr-2 h-4 w-4" />
                            {t("invoices.markAsSent")}
                          </DropdownMenuItem>
                        )}
                      {invoice.status === "sent" &&
                        invoice.invoiceType !== "free" && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(invoice.id, "paid");
                            }}
                          >
                            <CheckCircle className="mr-2 h-4 w-4" />
                            {t("invoices.markAsPaid")}
                          </DropdownMenuItem>
                        )}

                      {invoice.status === "draft" && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(invoice.id);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {t("invoices.delete")}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="text-muted-foreground grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="font-medium">{t("invoices.date")}:</span>{" "}
                    {formatDate(new Date(invoice.invoiceDate))}
                  </div>
                  <div className="text-right">
                    <span className="text-foreground text-sm font-medium">
                      {formatCurrency(invoice.total)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-muted-foreground py-8 text-center">
            {t("invoices.noInvoices")}
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden overflow-hidden rounded-md border sm:block">
        <div className="overflow-x-auto">
          <Table className="min-w-full">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const columnId = header.column.id;
                    const hiddenClass =
                      columnId === "dueDate"
                        ? "hidden md:table-cell"
                        : columnId === "purchaseOrderNumber"
                          ? "hidden lg:table-cell"
                          : columnId === "status"
                            ? "hidden sm:table-cell"
                            : "";
                    return (
                      <TableHead
                        key={header.id}
                        className={`px-2 py-2 sm:px-4 sm:py-3 ${hiddenClass}`}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={() => handleView(row.original.id)}
                    className="hover:bg-muted/50 cursor-pointer"
                  >
                    {row.getVisibleCells().map((cell) => {
                      const columnId = cell.column.id;
                      const hiddenClass =
                        columnId === "dueDate"
                          ? "hidden md:table-cell"
                          : columnId === "purchaseOrderNumber"
                            ? "hidden lg:table-cell"
                            : columnId === "status"
                              ? "hidden sm:table-cell"
                              : "";
                      return (
                        <TableCell
                          key={cell.id}
                          className={`px-1 py-2 sm:px-4 sm:py-3 ${hiddenClass}`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t("invoices.noInvoices")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-muted-foreground text-xs sm:text-sm">
          {invoiceData?.pagination && (
            <>
              {t("common.page")} {invoiceData.pagination.page} {t("common.of")}{" "}
              {invoiceData.pagination.totalPages} (
              {invoiceData.pagination.total} {t("invoices.title")}{" "}
              {t("common.total")})
            </>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="flex-1 sm:flex-initial"
          >
            {t("common.previous")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="flex-1 sm:flex-initial"
          >
            {t("common.next")}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invoices.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("invoices.confirmDelete")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setInvoiceToDelete(null)}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("invoices.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invoices.restoreTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("invoices.confirmRestore")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setInvoiceToRestore(null)}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRestore}
              className="bg-green-600 hover:bg-green-700"
            >
              {t("invoices.restore")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
